/**
 * Sidebar — product list with search, bulk actions, create.
 * Replaces window.Sidebar.
 */
import { useState, useRef, type FC } from 'react';
import { Icon } from './Icon';
import { ProductCard } from './ProductCard';
import type { Product } from '../stores/appStore';

interface Props {
  products: Product[];
  onSaveConfig: (products: Product[]) => void;
  onAddProduct: (files?: FileList) => void;
  onExport: () => void;
  onSaveCloud: () => void;
  isExporting: boolean;
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

export const Sidebar: FC<Props> = ({
  products,
  onSaveConfig,
  onAddProduct,
  activeTab,
  setActiveTab,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showToolsMenu, setShowToolsMenu] = useState(false);
  const toolsMenuRef = useRef<HTMLDivElement>(null);

  const filteredProducts = products.filter((p) =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleUpdate = (id: string, updates: Partial<Product>) => {
    onSaveConfig(products.map((p) => (p.id === id ? { ...p, ...updates } : p)));
  };
  const handleToggle = (id: string) => {
    onSaveConfig(products.map((p) => (p.id === id ? { ...p, enabled: !p.enabled } : p)));
  };
  const handleDelete = (id: string) => {
    if (!confirm('Удалить этот мокап?')) return;
    onSaveConfig(products.filter((p) => p.id !== id));
  };
  const handleSelectAll = () => onSaveConfig(products.map((p) => ({ ...p, enabled: true })));
  const handleDeselectAll = () => onSaveConfig(products.map((p) => ({ ...p, enabled: false })));
  const handleMove = (index: number, direction: 'up' | 'down') => {
    const newProducts = [...products];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newProducts.length) return;
    [newProducts[index], newProducts[targetIndex]] = [newProducts[targetIndex], newProducts[index]];
    onSaveConfig(newProducts);
  };
  const handleDuplicate = (product: Product) => {
    const newProd: Product = {
      ...product,
      id: 'copy_' + Date.now(),
      name: product.name + ' (Копия)',
      enabled: true,
    };
    const index = products.findIndex((p) => p.id === product.id);
    const newProducts = [...products];
    newProducts.splice(index + 1, 0, newProd);
    onSaveConfig(newProducts);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Tab Toggle + Tools */}
      <div className="flex items-center justify-between px-1">
        <div className="sidebar-tab-toggle">
          <div
            className="sidebar-tab-slider"
            style={{ transform: activeTab === 'products' ? 'translateX(100%)' : 'translateX(0)' }}
          />
          <button
            onClick={() => setActiveTab('mockups')}
            className={`sidebar-tab-btn ${activeTab === 'mockups' ? 'active' : ''}`}
          >
            Заготовки
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className={`sidebar-tab-btn ${activeTab === 'products' ? 'active' : ''}`}
          >
            Мокапы
          </button>
        </div>
        <button
          onClick={() => setShowToolsMenu((o) => !o)}
          className={`flex items-center justify-center w-7 h-7 rounded-lg border transition-all ${
            showToolsMenu
              ? 'bg-blue-600 border-blue-500 text-white rotate-45'
              : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-white hover:bg-slate-700'
          }`}
          title="Инструменты"
        >
          <Icon name="plus" className="w-4 h-4 transition-transform duration-200" />
        </button>
      </div>

      {/* Expandable Tools Menu */}
      <div
        ref={toolsMenuRef}
        style={{
          maxHeight: showToolsMenu
            ? toolsMenuRef.current
              ? toolsMenuRef.current.scrollHeight + 'px'
              : '200px'
            : '0',
          opacity: showToolsMenu ? 1 : 0,
          overflow: 'hidden',
          transition: 'max-height 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.2s ease',
        }}
      >
        <div className="flex flex-col gap-2 glass-card rounded-xl p-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-slate-400">Мокапов в списке</span>
            <span className="text-xs font-semibold text-slate-300">
              {filteredProducts.length} шт.
            </span>
          </div>
          <div className="flex gap-2">
            <label className="btn-primary flex-1 flex items-center justify-center gap-2 py-2 rounded-lg cursor-pointer font-medium group text-xs">
              <Icon name="upload" className="w-3.5 h-3.5" />
              <span>Из файла</span>
              <input
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files?.length) {
                    onAddProduct(e.target.files);
                    e.target.value = '';
                  }
                }}
              />
            </label>
            <button
              onClick={() => onAddProduct()}
              className="btn-secondary flex-1 flex items-center justify-center gap-2 py-2 rounded-lg font-medium text-xs"
            >
              <Icon name="plus" className="w-3.5 h-3.5" />
              <span>Пустой</span>
            </button>
          </div>
          <div className="search-box group">
            <Icon name="search" className="search-icon" />
            <input
              type="text"
              placeholder="Поиск мокапов..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-9"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSelectAll}
              className="btn-secondary flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-medium"
            >
              <Icon name="check-square" className="w-3.5 h-3.5" /> Выделить все
            </button>
            <button
              onClick={handleDeselectAll}
              className="btn-secondary flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-medium"
            >
              <Icon name="square" className="w-3.5 h-3.5" /> Снять все
            </button>
          </div>
        </div>
      </div>

      {/* Product List */}
      <div className="space-y-3 px-1 -mx-1">
        {filteredProducts.map((p, idx) => (
          <ProductCard
            key={p.id}
            product={p}
            index={idx}
            totalProducts={filteredProducts.length}
            allProducts={products}
            onToggle={handleToggle}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onMove={handleMove}
            onDuplicate={handleDuplicate}
            activeTab={activeTab}
          />
        ))}
        {filteredProducts.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-slate-500 gap-3 border-2 border-dashed border-slate-800 rounded-xl">
            <Icon name="search-x" className="w-8 h-8 opacity-50" />
            <span className="text-xs">Ничего не найдено</span>
          </div>
        )}
      </div>
    </div>
  );
};
